import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  ArrowLeft,
  DollarSign,
  GitMerge,
  Loader2,
  Pencil,
  ShoppingCart,
  TrendingUp,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  computeTrend,
  monthLabel,
  projectionFactor,
  type MonthCoverage,
  type MonthTotals,
} from '@afiliados/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/StatCard';
import { MonthRange } from '@/components/MonthControls';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { affiliatesApi, reportsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import {
  TREND_LABEL,
  TREND_TEXT_CLASS,
  fmtDay,
  fmtInt,
  fmtMoney,
  fmtTrend,
  fmtUsd,
  fmtUsdCompact,
} from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCan } from '@/features/auth/store';

const COLOR_GROSS = '#ea580c';
const COLOR_NET = '#0891b2';

/** "2026-04" → "2026-03" */
function previousMonthKey(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** Variação de cada mês contra o anterior (mesma regra do Planilhão, com projeção no parcial). */
function monthTrends(
  byMonth: MonthTotals[],
  coverage: MonthCoverage[],
  key: 'grossSales' | 'netSales',
) {
  const byKey = new Map(byMonth.map((m) => [m.month, m[key]]));
  const cov = new Map(coverage.map((c) => [c.month, c]));
  return byMonth.map((m) => {
    const c = cov.get(m.month);
    const prev = byKey.get(previousMonthKey(m.month)) ?? null;
    return computeTrend(
      m[key],
      prev,
      c ? projectionFactor(c.daysInMonth, c.daysCovered) : 1,
      c?.partial ?? false,
    );
  });
}

export function AffiliateDetailPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan('affiliates.manage');
  const months = useQuery({ queryKey: ['months'], queryFn: reportsApi.months, staleTime: 60_000 });
  const available = months.data ?? [];
  const from = params.get('from') ?? available[0] ?? '';
  const to = params.get('to') ?? available[available.length - 1] ?? '';
  const q = useQuery({
    queryKey: ['affiliates', id, from, to],
    queryFn: () => affiliatesApi.get(id, { from: from || undefined, to: to || undefined }),
    enabled: !!id && !months.isLoading,
  });
  const a = q.data;

  const [edit, setEdit] = useState(false);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (a) {
      setName(a.name);
      setNotes(a.notes ?? '');
    }
  }, [a]);
  const [mergeTarget, setMergeTarget] = useState<{ id: string; name: string } | null>(null);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeOpen, setMergeOpen] = useState(false);
  const candidates = useQuery({
    queryKey: ['affiliates', 'search', mergeSearch],
    queryFn: () => affiliatesApi.search(mergeSearch),
    enabled: mergeOpen && mergeSearch.trim().length >= 2,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['affiliates'] });
    void qc.invalidateQueries({ queryKey: ['reports'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const save = useMutation({
    mutationFn: () => affiliatesApi.update(id, { name: name.trim(), notes: notes.trim() || null }),
    onSuccess: () => {
      toast.success('Afiliado atualizado');
      setEdit(false);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar'),
  });
  const merge = useMutation({
    mutationFn: (sourceId: string) => affiliatesApi.merge(id, sourceId),
    onSuccess: () => {
      toast.success('Cadastros unidos');
      setMergeTarget(null);
      setMergeOpen(false);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao unir'),
  });

  if (q.isLoading || !a) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const grossTrends = monthTrends(a.byMonth, a.coverage, 'grossSales');

  return (
    <div>
      <PageHeader
        title={a.name}
        description={a.notes ?? 'Faturamento do afiliado nos relatórios importados'}
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <MonthRange
              months={available}
              from={from}
              to={to}
              onChange={(f, t) => setParams({ from: f, to: t }, { replace: true })}
            />
            {canManage && (
              <>
                <Button variant="outline" onClick={() => setEdit(true)}>
                  <Pencil /> Editar
                </Button>
                <Button variant="outline" onClick={() => setMergeOpen(true)}>
                  <GitMerge /> Unir outro cadastro
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft /> Voltar
            </Button>
          </div>
        }
      />

      {a.possibleDuplicates.length > 0 && (
        <Card className="mb-4 border-warning/50">
          <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm">
            <Users className="size-4 text-amber-600" />
            <span>
              Cadastros com nome parecido (só os espaços diferem), que podem ser a mesma pessoa:
            </span>
            {a.possibleDuplicates.map((d) => (
              <span key={d.id} className="flex items-center gap-1">
                <Link to={`/afiliados/${d.id}`} className="font-medium underline">
                  {d.name}
                </Link>
                <span className="text-xs text-muted-foreground">
                  (líq. {fmtMoney(d.totals.netSales)})
                </span>
                {canManage && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setMergeTarget({ id: d.id, name: d.name })}
                  >
                    Unir a este
                  </Button>
                )}
              </span>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={DollarSign}
          label="Bruto"
          value={fmtUsd(a.totals.grossSales)}
          hint={`${fmtInt(a.totals.orders)} pedidos`}
        />
        <StatCard
          icon={TrendingUp}
          label="Líquido"
          value={fmtUsd(a.totals.netSales)}
          hint={`comissões ${fmtUsd(a.totals.commissionsNet)}`}
          tone={a.totals.netSales < 0 ? 'destructive' : 'default'}
        />
        <StatCard
          icon={ShoppingCart}
          label="Clientes"
          value={fmtInt(a.totals.customers)}
          hint={`${fmtInt(a.totals.visitors)} visitantes`}
        />
        <StatCard
          icon={Users}
          label="Meses com relatório"
          value={a.monthsActive}
          hint={
            a.firstPeriod
              ? `${fmtDay(a.firstPeriod)} – ${fmtDay(a.lastPeriod)}`
              : 'sem linhas no período'
          }
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Por mês</CardTitle>
            <CardDescription>Bruto e líquido somando todos os produtos</CardDescription>
          </CardHeader>
          <CardContent>
            {a.byMonth.length ? (
              <>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={a.byMonth} margin={{ left: 0, right: 8, top: 8 }}>
                      <CartesianGrid vertical={false} stroke="var(--border)" />
                      <XAxis
                        dataKey="month"
                        tickFormatter={monthLabel}
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={(v: number) => fmtUsdCompact(v)}
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                        axisLine={false}
                        tickLine={false}
                        width={72}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'var(--popover)',
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                          fontSize: 12,
                          color: 'var(--foreground)',
                        }}
                        labelFormatter={(m) => monthLabel(String(m))}
                        formatter={(v, name) => [
                          fmtUsd(Number(v)),
                          name === 'grossSales' ? 'Bruto' : 'Líquido',
                        ]}
                      />
                      <Bar dataKey="grossSales" fill={COLOR_GROSS} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="netSales" fill={COLOR_NET} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <Table className="mt-4">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mês</TableHead>
                      <TableHead className="text-right">Bruto</TableHead>
                      <TableHead
                        className="text-right"
                        title="Variação do bruto contra o mês anterior"
                      >
                        Variação
                      </TableHead>
                      <TableHead className="text-right">Líquido</TableHead>
                      <TableHead className="text-right">Comissões</TableHead>
                      <TableHead className="text-right">Pedidos</TableHead>
                      <TableHead className="text-right">Visitantes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {a.byMonth.map((m, i) => (
                      <TableRow key={m.month}>
                        <TableCell className="font-medium">
                          {monthLabel(m.month)}
                          {a.coverage.find((c) => c.month === m.month)?.partial && (
                            <span
                              className="ml-1 rounded bg-amber-400/30 px-1 text-[10px] font-normal text-amber-800 dark:text-amber-200"
                              title="Só parte dos dias foi importada; a variação usa a projeção"
                            >
                              parcial
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(m.grossSales)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {grossTrends[i]?.status ? (
                            <span
                              className={TREND_TEXT_CLASS[grossTrends[i]!.status!]}
                              title={TREND_LABEL[grossTrends[i]!.status!]}
                            >
                              {fmtTrend(grossTrends[i])}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right tabular-nums',
                            m.netSales < 0 && 'text-destructive',
                          )}
                        >
                          {fmtMoney(m.netSales)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(m.commissionsNet)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtInt(m.orders)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtInt(m.visitors)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Sem linhas no período selecionado.</p>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Por produto</CardTitle>
              <CardDescription>No período</CardDescription>
            </CardHeader>
            <CardContent>
              {a.byProduct.length ? (
                <ul className="divide-y">
                  {a.byProduct.map((p) => (
                    <li
                      key={p.productId}
                      className="flex items-center justify-between gap-2 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{p.productName}</p>
                        <p className="text-xs text-muted-foreground">
                          {fmtInt(p.orders)} pedido(s) · bruto {fmtMoney(p.grossSales)}
                        </p>
                      </div>
                      <span
                        className={cn(
                          'shrink-0 tabular-nums',
                          p.netSales < 0 && 'text-destructive',
                        )}
                      >
                        {fmtMoney(p.netSales)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Sem produtos no período.</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Nomes nos relatórios</CardTitle>
              <CardDescription>Como este afiliado apareceu nos arquivos</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-1">
              {a.aliases.map((al) => (
                <Badge key={al.rawName} variant="secondary" className="font-mono text-xs">
                  {al.rawName.replace(/ /g, '␣')}
                </Badge>
              ))}
              <p className="mt-2 w-full text-xs text-muted-foreground">
                ␣ marca cada espaço: a BuyGoods preserva espaços duplicados e nas pontas.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar afiliado</DialogTitle>
            <DialogDescription>
              O nome exibido não muda como as próximas importações o reconhecem (isso vem dos nomes
              dos relatórios).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="aff-name">Nome</Label>
              <Input id="aff-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="aff-notes">Observações</Label>
              <Textarea
                id="aff-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex.: equipe de tráfego, contato, acordo comercial…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(false)}>
              Cancelar
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || !name.trim()}>
              {save.isPending && <Loader2 className="animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unir outro cadastro a {a.name}</DialogTitle>
            <DialogDescription>
              As linhas e os nomes do cadastro escolhido passam para este. Use quando o mesmo
              afiliado aparece com nomes diferentes nos relatórios.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Buscar pelo nome…"
            value={mergeSearch}
            onChange={(e) => setMergeSearch(e.target.value)}
            autoFocus
          />
          <div className="max-h-64 overflow-auto rounded border">
            {candidates.data
              ?.filter((c) => c.id !== id)
              .map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => setMergeTarget({ id: c.id, name: c.name })}
                >
                  <span>{c.name}</span>
                  <GitMerge className="size-4 text-muted-foreground" />
                </button>
              ))}
            {mergeSearch.trim().length < 2 && (
              <p className="p-3 text-xs text-muted-foreground">Digite pelo menos 2 letras.</p>
            )}
            {candidates.data && !candidates.data.filter((c) => c.id !== id).length && (
              <p className="p-3 text-xs text-muted-foreground">Nenhum afiliado encontrado.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!mergeTarget} onOpenChange={(o) => !o && setMergeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Unir "{mergeTarget?.name}" a "{a.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Todas as linhas de relatório de{' '}
              <span className="font-medium text-foreground">{mergeTarget?.name}</span> passam a
              contar para <span className="font-medium text-foreground">{a.name}</span>, inclusive
              nas próximas importações. O cadastro unido deixa de existir. A ação fica registrada na
              auditoria e não pode ser desfeita pelo painel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => mergeTarget && merge.mutate(mergeTarget.id)}>
              Unir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
